import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { IQuestionPage } from 'services/campaignPageLogic.service';
import { IQuestionNewEditForm } from 'types/campaign-design';
import { handleError, createStyleSheet } from 'utils/helpers';
import { useParams } from 'react-router-dom';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import QuotaTable from './quota-table';
import { createQuota, ICreateQuotaPayload } from 'services/quota.service';

interface RewardFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    questionPages?: IQuestionPage[];
}

interface IQuotaCondition {
    question_id: number | null;
    answer_option_id: number | null;
}

interface IQuotaSelectionData {
    name: string;
    limit: number;
    question_page_id: number | null;
    conditions: IQuotaCondition[];
}

interface AnswerOption {
    id: number;
    text: string;
}

const QuotaForm: React.FC<RewardFormProps> = ({
    open,
    onClose,
    onSuccess,
    questionPages,
}) => {
    const { id: surveyId } = useParams<{ id: string }>();
    const [saving, setSaving] = useState(false);
    const [refreshQuotas, setRefreshQuotas] = useState(0);
    const [quotaData, setQuotaData] = useState<IQuotaSelectionData>({
        name: '',
        limit: 1,
        question_page_id: null,
        conditions: [],
    });
    const [tempCondition, setTempCondition] = useState<IQuotaCondition>({
        question_id: null,
        answer_option_id: null,
    });

    // Format questions using listCampaignQuestionsFormatter
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formatQuestion = (question: any): IQuestionNewEditForm => {
        // If already formatted, return as is
        if (!question.attributes && question.questionType) {
            return question;
        }
        // If raw API response, format it
        return listCampaignQuestionsFormatter(question);
    };

    // Get answer options from a question
    const getAnswerOptions = (
        question: IQuestionNewEditForm
    ): AnswerOption[] => {
        const options: AnswerOption[] = [];
        if (
            question.data &&
            (question.questionType === 'single_choice' ||
                question.questionType === 'dropdown')
        ) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const data = question.data as any;
            if (data.answers && Array.isArray(data.answers)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return data.answers.map((ans: any, idx: number) => ({
                    id: ans.id,
                    text: ans.text || ans.answer || `Option ${idx + 1}`,
                }));
            }
        }

        return options;
    };

    // Handle quota name change
    const handleNameChange = (value: string) => {
        setQuotaData((prev) => ({
            ...prev,
            name: value,
        }));
    };

    // Handle limit change
    const handleLimitChange = (value: string) => {
        if (value !== '' && !/^\d+$/.test(value)) return;
        const numValue = value === '' ? 1 : Math.max(1, parseInt(value, 10));
        setQuotaData((prev) => ({
            ...prev,
            limit: numValue,
        }));
    };

    // Handle page selection
    const handlePageSelect = (pageId: number | null) => {
        setQuotaData((prev) => ({
            ...prev,
            question_page_id: pageId,
        }));
        setTempCondition({
            question_id: null,
            answer_option_id: null,
        });
    };

    // Handle temp condition question change
    const handleTempQuestionChange = (questionId: number | null) => {
        setTempCondition((prev) => ({
            ...prev,
            question_id: questionId,
            answer_option_id: null,
        }));
    };

    // Handle temp condition answer option change
    const handleTempAnswerOptionChange = (answerOptionId: number | null) => {
        setTempCondition((prev) => ({
            ...prev,
            answer_option_id: answerOptionId,
        }));
    };

    // Add condition to quota
    const handleAddCondition = () => {
        if (
            tempCondition.question_id === null ||
            tempCondition.answer_option_id === null
        ) {
            toast.warning('Vui lòng chọn câu hỏi và tùy chọn trả lời');
            return;
        }

        // Check if question is already selected
        const selectedQuestionIds = getSelectedQuestionIds();
        if (selectedQuestionIds.includes(tempCondition.question_id)) {
            toast.warning('Câu hỏi này đã được chọn rồi');
            return;
        }

        // Check if question is from the selected page
        const questionPageId = getPageIdByQuestionId(tempCondition.question_id);
        if (questionPageId !== effectivePageId) {
            toast.warning(
                'Câu hỏi phải thuộc trang đã được chọn: ' +
                    questionPages?.find(
                        (p) => p.attributes.id === effectivePageId
                    )?.attributes.title
            );
            return;
        }

        // Check if condition already exists
        const exists = quotaData.conditions.some(
            (cond) =>
                cond.question_id === tempCondition.question_id &&
                cond.answer_option_id === tempCondition.answer_option_id
        );

        if (exists) {
            toast.warning('Điều kiện này đã được thêm');
            return;
        }

        setQuotaData((prev) => ({
            ...prev,
            conditions: [...prev.conditions, { ...tempCondition }],
        }));

        setTempCondition({
            question_id: null,
            answer_option_id: null,
        });
    };

    // Remove condition from quota
    const handleRemoveCondition = (index: number) => {
        setQuotaData((prev) => ({
            ...prev,
            conditions: prev.conditions.filter((_, i) => i !== index),
        }));
    };

    // Get page ID by question ID
    const getPageIdByQuestionId = (questionId: number): number | null => {
        for (const page of questionPages || []) {
            const question = page.attributes.questions?.find(
                (q) => Number(q.id) === questionId
            );
            if (question) {
                return page.attributes.id;
            }
        }
        return null;
    };

    // Get page ID from first condition if it exists
    const getPageIdFromConditions = (): number | null => {
        if (quotaData.conditions.length > 0) {
            const firstCondition = quotaData.conditions[0];
            return getPageIdByQuestionId(firstCondition.question_id || 0);
        }
        return null;
    };

    // Determine the effective page ID to use for validation
    const effectivePageId =
        quotaData.conditions.length > 0
            ? getPageIdFromConditions()
            : quotaData.question_page_id;

    // Get list of already selected question IDs
    const getSelectedQuestionIds = () => {
        return quotaData.conditions.map((cond) => cond.question_id);
    };

    // Get question by ID
    const getQuestionById = (
        questionId: number
    ): IQuestionNewEditForm | null => {
        for (const page of questionPages || []) {
            const question = page.attributes.questions?.find(
                (q) => Number(q.id) === questionId
            );
            if (question) {
                return formatQuestion(question);
            }
        }
        return null;
    };

    // Handle save
    const handleSave = async () => {
        if (!surveyId) return;

        // Validate quota data
        if (!quotaData.name.trim()) {
            toast.warning('Vui lòng nhập tên quota');
            return;
        }

        if (quotaData.limit < 1) {
            toast.warning('Giới hạn quota phải lớn hơn 0');
            return;
        }

        if (quotaData.conditions.length === 0) {
            toast.warning('Vui lòng thêm ít nhất một điều kiện');
            return;
        }

        setSaving(true);
        try {
            const payload: ICreateQuotaPayload = {
                quota: {
                    survey_id: Number(surveyId),
                    question_page_id: quotaData.question_page_id,
                    name: quotaData.name,
                    limit: quotaData.limit,
                    conditions: quotaData.conditions.map((cond) => ({
                        question_id: cond.question_id || 0,
                        answer_option_id: cond.answer_option_id || 0,
                    })),
                },
            };

            await createQuota(payload);

            toast.success('Thiết lập quota thành công');

            // Refresh quota table
            setRefreshQuotas((prev) => prev + 1);

            onSuccess?.();

            // Reset form
            setQuotaData({
                name: '',
                limit: 1,
                question_page_id: null,
                conditions: [],
            });
            setTempCondition({
                question_id: null,
                answer_option_id: null,
            });
        } catch (error) {
            handleError(error);
        } finally {
            setSaving(false);
        }
    };

    // Handle close
    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6" fontWeight="bold">
                        Quản lý Quota
                    </Typography>
                    <IconButton onClick={handleClose} disabled={saving}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {!questionPages || questionPages.length === 0 ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={300}
                    >
                        <Typography color="text.secondary">
                            Chưa có trang câu hỏi nào. Vui lòng tạo trang câu
                            hỏi trước.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {/* Quota List Table */}
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ mb: 2 }}
                            >
                                Danh sách Quotas
                            </Typography>
                            {surveyId && (
                                <QuotaTable
                                    surveyId={Number(surveyId)}
                                    refreshTrigger={refreshQuotas}
                                    questionPages={questionPages}
                                />
                            )}
                        </Box>

                        <Divider />

                        {/* Create New Quota Form */}
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ mb: 2 }}
                            >
                                Tạo Quota Mới
                            </Typography>
                            <Stack spacing={3}>
                                {/* Quota Name */}
                                <TextField
                                    fullWidth
                                    label="Tên Quota"
                                    placeholder="VD: Quota nam 18-25"
                                    value={quotaData.name}
                                    onChange={(e) =>
                                        handleNameChange(e.target.value)
                                    }
                                    disabled={saving}
                                    size="small"
                                />

                                {/* Quota Limit */}
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Giới hạn (Limit)"
                                    value={quotaData.limit}
                                    onChange={(e) =>
                                        handleLimitChange(e.target.value)
                                    }
                                    disabled={saving}
                                    inputProps={{ min: 1 }}
                                    size="small"
                                />

                                {/* Conditions Section */}
                                <Box
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        p: 2,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="600"
                                        sx={{ mb: 2 }}
                                    >
                                        Điều kiện Quota (AND Logic)
                                    </Typography>

                                    {/* Add Condition Form */}
                                    <Stack
                                        spacing={2}
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            backgroundColor: '#f9f9f9',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Bước 1: Chọn trang câu hỏi, Bước 2:
                                            Chọn câu hỏi, Bước 3: Chọn tùy chọn
                                            trả lời
                                        </Typography>

                                        {/* Page Selector */}
                                        <FormControl fullWidth size="small">
                                            <InputLabel>
                                                Trang Câu Hỏi
                                            </InputLabel>
                                            <Select
                                                value={
                                                    quotaData.question_page_id ||
                                                    ''
                                                }
                                                label="Trang Câu Hỏi"
                                                onChange={(e) =>
                                                    handlePageSelect(
                                                        e.target.value
                                                            ? Number(
                                                                  e.target.value
                                                              )
                                                            : null
                                                    )
                                                }
                                                disabled={
                                                    saving ||
                                                    quotaData.conditions
                                                        .length > 0
                                                }
                                            >
                                                <MenuItem value="">
                                                    -- Chọn trang --
                                                </MenuItem>
                                                {questionPages?.map((page) => (
                                                    <MenuItem
                                                        key={page.attributes.id}
                                                        value={String(
                                                            page.attributes.id
                                                        )}
                                                    >
                                                        {page.attributes
                                                            .title ||
                                                            `Page ${page.attributes.page_number}`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Question Selector */}
                                        {quotaData.question_page_id && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Câu Hỏi</InputLabel>
                                                <Select
                                                    value={
                                                        tempCondition.question_id ||
                                                        ''
                                                    }
                                                    label="Câu Hỏi"
                                                    onChange={(e) =>
                                                        handleTempQuestionChange(
                                                            e.target.value
                                                                ? Number(
                                                                      e.target
                                                                          .value
                                                                  )
                                                                : null
                                                        )
                                                    }
                                                    disabled={saving}
                                                >
                                                    <MenuItem value="">
                                                        -- Chọn câu hỏi --
                                                    </MenuItem>
                                                    {questionPages
                                                        ?.find(
                                                            (page) =>
                                                                Number(
                                                                    page
                                                                        .attributes
                                                                        .id
                                                                ) ===
                                                                quotaData.question_page_id
                                                        )
                                                        ?.attributes.questions?.filter(
                                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                            (q: any) =>
                                                                (q.attributes
                                                                    .kind ===
                                                                    'single_choice' ||
                                                                    q.attributes
                                                                        .kind ===
                                                                        'dropdown') &&
                                                                !getSelectedQuestionIds().includes(
                                                                    Number(
                                                                        q
                                                                            .attributes
                                                                            .id
                                                                    )
                                                                )
                                                        )
                                                        .map((question) => {
                                                            const formatted =
                                                                formatQuestion(
                                                                    question
                                                                );
                                                            return (
                                                                <MenuItem
                                                                    key={
                                                                        formatted.id
                                                                    }
                                                                    value={String(
                                                                        formatted.id
                                                                    )}
                                                                >
                                                                    {formatted.questionNumber
                                                                        ? `Q${formatted.questionNumber}: ${formatted.question}`
                                                                        : formatted.question}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                </Select>
                                            </FormControl>
                                        )}

                                        {/* Answer Option Selector */}
                                        {tempCondition.question_id && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>
                                                    Tùy Chọn Trả Lời
                                                </InputLabel>
                                                <Select
                                                    value={
                                                        tempCondition.answer_option_id ||
                                                        ''
                                                    }
                                                    label="Tùy Chọn Trả Lời"
                                                    onChange={(e) =>
                                                        handleTempAnswerOptionChange(
                                                            e.target.value
                                                                ? Number(
                                                                      e.target
                                                                          .value
                                                                  )
                                                                : null
                                                        )
                                                    }
                                                    disabled={saving}
                                                >
                                                    <MenuItem value="">
                                                        -- Chọn tùy chọn --
                                                    </MenuItem>
                                                    {getAnswerOptions(
                                                        getQuestionById(
                                                            tempCondition.question_id
                                                        ) ||
                                                            ({} as IQuestionNewEditForm)
                                                    ).map((option) => (
                                                        <MenuItem
                                                            key={option.id}
                                                            value={String(
                                                                option.id
                                                            )}
                                                        >
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}

                                        {/* Add Button */}
                                        <Button
                                            variant="outlined"
                                            onClick={handleAddCondition}
                                            disabled={
                                                saving ||
                                                tempCondition.question_id ===
                                                    null ||
                                                tempCondition.answer_option_id ===
                                                    null
                                            }
                                        >
                                            Thêm Điều kiện
                                        </Button>
                                    </Stack>

                                    {/* Conditions List */}
                                    {quotaData.conditions.length > 0 ? (
                                        <Stack spacing={1}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {quotaData.conditions.length}{' '}
                                                điều kiện được thêm:
                                            </Typography>
                                            {quotaData.conditions.map(
                                                (condition, index) => {
                                                    const question =
                                                        getQuestionById(
                                                            condition.question_id ||
                                                                0
                                                        );
                                                    const answerOptions =
                                                        question
                                                            ? getAnswerOptions(
                                                                  question
                                                              )
                                                            : [];
                                                    const selectedOption =
                                                        answerOptions.find(
                                                            (opt) =>
                                                                opt.id ===
                                                                condition.answer_option_id
                                                        );
                                                    const pageId =
                                                        getPageIdByQuestionId(
                                                            condition.question_id ||
                                                                0
                                                        );
                                                    const pageTitle =
                                                        questionPages?.find(
                                                            (page) =>
                                                                page.attributes
                                                                    .id ===
                                                                pageId
                                                        )?.attributes.title;

                                                    return (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                p: 1.5,
                                                                backgroundColor:
                                                                    '#e3f2fd',
                                                                borderRadius: 1,
                                                                border: '1px solid #90caf9',
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'space-between',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                        >
                                                            <Typography variant="body2">
                                                                <strong>
                                                                    Trang:
                                                                </strong>{' '}
                                                                {pageTitle} |{' '}
                                                                <strong>
                                                                    Câu hỏi:
                                                                </strong>{' '}
                                                                {question?.question ||
                                                                    `Q${question?.questionNumber}`}{' '}
                                                                |{' '}
                                                                <strong>
                                                                    Tùy chọn:
                                                                </strong>{' '}
                                                                {
                                                                    selectedOption?.text
                                                                }
                                                            </Typography>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                onClick={() =>
                                                                    handleRemoveCondition(
                                                                        index
                                                                    )
                                                                }
                                                                disabled={
                                                                    saving
                                                                }
                                                            >
                                                                Xóa
                                                            </Button>
                                                        </Box>
                                                    );
                                                }
                                            )}
                                        </Stack>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontStyle: 'italic' }}
                                        >
                                            Chưa có điều kiện nào. Hãy thêm ít
                                            nhất một.
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Hủy
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={
                        saving ||
                        !quotaData.name.trim() ||
                        quotaData.limit < 1 ||
                        quotaData.conditions.length === 0
                    }
                    sx={styles.button}
                >
                    {saving ? <CircularProgress size={20} /> : 'Lưu Quota'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const styles = createStyleSheet({
    button: {
        minWidth: '160px',
        minHeight: '36px',
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 700,
    },
});
export default QuotaForm;
